import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { LazyScrollableDataSource } from '@rappi/common';
import { AlertsType } from '@rappi/ui/alerts';
import { Observable, Subject, forkJoin, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { AlertsService } from '../../../services/alerts/alerts.service';
import { StoreAuxService } from '../../../services/storeAux/store-aux.service';
import { HasPermissionsService } from '../../../shared/services/has-permissions/has-permissions.service';
import { CompaniesFormConfigService } from '../../services/companies-form-config.service';
import { MakersFinancesMgrService } from '../../services/makers-finances-mgr.service';
import { AgencyRebate, CompanyRecord } from '../../models/makers-finances-mgr.models';
import {
  COMPANY_PERMISSIONS,
  COMPANY_QUERY_FROM,
  COMPANY_QUERY_PARAM,
  COMPANY_STATUS,
  normalizeCompanyStatus,
} from '../companies/companies.constants';
import { DETAIL_MODE, DetailMode } from '../purchase-order-detail/purchase-order.constants';
import { SelectOption } from '../purchase-order-detail/sections/section-base.types';
import { FhOptionResolver } from '../shared/fh-select/fh-select.component';
import { AgencyRebatesCardComponent } from './components/agency-rebates-card/agency-rebates-card.component';
import { CompanyGroupDialogComponent } from './dialogs/company-group-dialog/company-group-dialog.component';
import { ContactFormDialogComponent } from '../shared/contact-form-dialog/contact-form-dialog.component';
import {
  FH_COMPANY_LABEL,
  FH_COMPANY_MESSAGE,
  FH_FIELD,
  FH_FIELD_TYPE,
  FH_FLAG,
  FH_PAYLOAD,
  FH_REBATE,
  FH_SECTION_KIND,
  FH_SERVICE_TYPE,
  FH_YES_NO,
} from './company-form.constants';
import { CompanyFieldFactory } from './company-form.factory';
import { buildCompanyPayload, sectionStateKey } from './company-form.payload';
import {
  isFieldEditable,
  isFieldRequired,
  isFieldVisible,
  isSectionVisible,
} from './company-form.rules';
import { buildStaticValidators, fhDuplicateThresholdValidator } from './company-form.validators';
import {
  CompaniesFormConfig,
  CompanyConfig,
  CompanyDocumentTypeConfig,
  CompanyFieldConfig,
  CompanyFieldDescriptor,
  CompanyFormSection,
  FhFieldState,
  FhPredicateContext,
  FhRebateRow,
} from './company-form.types';

// Orchestrates the config-driven company form for both `companies/create` and
// `companies/:id/edit`.
//
// The layout config (companies-form.config.json) and the per-country backend field config are
// merged into descriptors by CompanyFieldFactory; the rules engine decides visibility and
// required-ness on every value change; the payload builder turns the form back into a request body
// keyed by each field's `payload`.
//
// Unlike the PO form, the selects here are real ControlValueAccessors (fh-select), so edit-mode
// prefill is a plain patchValue with no setSelection dance.
@Component({
  selector: 'app-company-form',
  templateUrl: './company-form.component.html',
  styleUrls: ['./company-form.component.scss'],
})
export class CompanyFormComponent implements OnInit, OnDestroy {
  readonly LABELS = FH_COMPANY_LABEL;
  readonly SECTION_KIND = FH_SECTION_KIND;
  readonly MODE = DETAIL_MODE;

  mode: DetailMode = DETAIL_MODE.CREATE;
  companyId: number | null = null;
  // When the edit route is reached from the list (?from=list — only sent for Draft companies, see
  // companies.component.viewCompany), Cancel/save-success return to the list instead of the
  // read-only detail view.
  cameFromList = false;

  form: FormGroup;
  sections: CompanyFormSection[] = [];
  states = new Map<string, FhFieldState>();
  sources: Record<string, LazyScrollableDataSource<SelectOption>> = {};
  resolvers: Record<string, FhOptionResolver> = {};

  // CMS `company_documents.types`, keyed by field_name (== a FILE field's `payload`). `null` means
  // the CMS response omitted the key entirely (old format — see
  // docs/documents/COMPANY_DOCUMENTS_ARCHITECTURE.md §8/§12): any field_name is accepted, no
  // limits. A present Map, even empty, means strict mode: only listed field_names are accepted.
  documentTypesByFieldName: Map<string, CompanyDocumentTypeConfig> | null = null;

  configLoaded = false;
  configReloading = false;
  submitted = false;
  isSaving = false;
  companyLoaded = false;

  country = '';

  private rawConfig: CompaniesFormConfig | null = null;
  private company: CompanyRecord | null = null;
  private byFieldId = new Map<string, string>();
  private byControlName = new Map<string, CompanyFieldDescriptor>();
  // Maps a company-type `code` (the value fh-select holds for the type_of_company field) back to
  // its numeric catalog id (CatalogItem.id). Populated as the type-of-company select pages load,
  // and seeded in edit mode so the saved code resolves without the user re-opening the dropdown.
  // Lets _persist emit type_of_company_id alongside the code (see FH_PAYLOAD.TYPE_OF_COMPANY_ID).
  private readonly companyTypeIdByCode = new Map<string, number>();
  private readonly staticValidators = new Map<string, ValidatorFn[]>();
  private readonly requiredApplied = new Map<string, boolean>();
  // Tracks the flag the current backend config was fetched with, so a no-op change does not refetch.
  private lastIsInternational = false;
  private recomputing = false;
  private readonly destroy$ = new Subject<void>();

  /* istanbul ignore next -- decorator metadata */
  constructor(
    private readonly fb: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly configService: CompaniesFormConfigService,
    private readonly financesService: MakersFinancesMgrService,
    private readonly storeAux: StoreAuxService,
    private readonly alerts: AlertsService,
    private readonly permissionsService: HasPermissionsService,
  ) {}

  // ─── Lifecycle ───────────────────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.mode = this.route.snapshot.data['mode'] || DETAIL_MODE.CREATE;
    this.country = this.financesService.resolveCountry(this.storeAux.country);

    if (this.mode === DETAIL_MODE.EDIT && !this._initEditMode()) {
      return;
    }

    forkJoin({
      json: this.configService.getConfig(),
      backend: this.financesService.getCompanyFieldConfig(this.country, false).pipe(
        catchError(() => {
          this.alerts.openAlerts(FH_COMPANY_MESSAGE.CONFIG_FAILED, AlertsType.error);
          return of(null);
        }),
      ),
    }).pipe(takeUntil(this.destroy$))
      .subscribe(({ json, backend }) => {
        this.rawConfig = json;
        // Must run before _rebuild (which recomputes field state including required-ness) so a
        // FILE field's CMS `min` is already known on the very first pass.
        this._applyDocumentConfig(backend?.data ?? null);
        this._rebuild(backend?.data?.fields ?? null);
        this._watchDependencies();
        this._watchInternationalCompany();
        this.configLoaded = true;
        this._tryPatchForm();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private _initEditMode(): boolean {
    const idParam = this.route.snapshot.paramMap.get('id');
    const id = Number(idParam);
    if (!idParam || !Number.isFinite(id)) {
      this.router.navigate(['/financial-hub/companies']);
      return false;
    }
    this.companyId = id;
    this.cameFromList = this.route.snapshot.queryParamMap.get(COMPANY_QUERY_PARAM.FROM) === COMPANY_QUERY_FROM.LIST;
    this.financesService.getCompany(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          this.company = res.data;
          // Seed code → id so a save that never re-opens the type dropdown can still emit
          // type_of_company_id (the detail record carries both the code and the numeric id).
          if (res.data.type_of_company && res.data.type_of_company_id != null) {
            this.companyTypeIdByCode.set(res.data.type_of_company, res.data.type_of_company_id);
          }
          this._tryPatchForm();
        },
        error: () => {
          this.alerts.openAlerts(FH_COMPANY_MESSAGE.LOAD_FAILED, AlertsType.error);
          this.router.navigate(['/financial-hub/companies']);
        },
      });
    return true;
  }

  // ─── Build ───────────────────────────────────────────────────────────────────────────────────

  // Rebuilds descriptors and the form from a (possibly null) backend field config. Called on load
  // and again whenever `international_company` flips.
  private _rebuild(backendFields: CompanyFieldConfig[] | null): void {
    const previous = this.form ? this.form.getRawValue() : null;

    this.sections = CompanyFieldFactory.buildSections(this.rawConfig, backendFields);
    this.byControlName = CompanyFieldFactory.indexByControlName(this.sections);
    this.byFieldId = CompanyFieldFactory.indexControlNameByFieldId(this.sections);

    this._buildForm();
    if (previous) {
      // Values for fields that survived the config swap are restored; fields that disappeared are
      // intentionally dropped.
      this.form.patchValue(previous, { emitEvent: false });
    }
    this._applySingleOptionDefaults();
    this._buildSelectSources();
    this._recompute();
  }

  // A SELECT the backend resolved to exactly one option auto-fills with it (see
  // CompanyFieldFactory.buildDescriptor's `singleOption` and isFieldEditable in company-form.rules,
  // which locks the control read-only). Skipped when a value already survived the config swap above.
  private _applySingleOptionDefaults(): void {
    this.sections.forEach(section => section.fields.forEach(field => {
      if (!field.singleOption) {
        return;
      }
      const control = this.form.get(field.controlName);
      if (control && !control.value) {
        control.setValue(field.options[0].id, { emitEvent: false });
      }
    }));
  }

  private _buildForm(): void {
    const controls: Record<string, unknown> = {};

    this.sections.forEach(section => {
      if (section.kind === FH_SECTION_KIND.REBATES) {
        return;
      }
      section.fields.forEach(field => {
        const validators = buildStaticValidators(field);
        this.staticValidators.set(field.controlName, validators);
        controls[field.controlName] = [this._initialValue(field), validators];
      });
    });

    const group = this.fb.group(controls);
    group.addControl(
      FH_FIELD.AGENCY_REBATES,
      this.fb.array([], fhDuplicateThresholdValidator()),
    );
    this.form = group;
    this.requiredApplied.clear();
  }

  private _initialValue(field: CompanyFieldDescriptor): unknown {
    if (field.type === FH_FIELD_TYPE.FILE) {
      return [];
    }
    if (field.type === FH_FIELD_TYPE.BOOLEAN) {
      return CompanyFieldFactory.resolveBooleanDefault(field.defaultValue);
    }
    return field.defaultValue ?? null;
  }

  // ─── Dependency engine ───────────────────────────────────────────────────────────────────────

  private _watchDependencies(): void {
    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this._recompute());
  }

  // Toggling `international_company` changes the fiscal field set, so the backend config is
  // refetched. switchMap cancels an in-flight request if the user toggles twice quickly.
  private _watchInternationalCompany(): void {
    const controlName = this.byFieldId.get(FH_FIELD.INTERNATIONAL_COMPANY);
    if (!controlName) {
      return;
    }
    this.form.get(controlName)?.valueChanges.pipe(
      // Tolerates both conventions: a legacy "yes"/"no" select and international_company's current
      // BOOLEAN type, whose control holds a real boolean initially and the stringified "true"/
      // "false" id once the select is touched (see FH_BOOLEAN_OPTIONS).
      map(value => value === true || value === FH_FLAG.TRUE || value === FH_YES_NO.YES),
      // Seeded against the last known value rather than distinctUntilChanged(), which has no
      // prior value on its first emission and would refetch even when nothing actually changed.
      filter(isInternational => isInternational !== this.lastIsInternational),
      tap(isInternational => {
        this.lastIsInternational = isInternational;
      }),
      switchMap(isInternational => {
        this.configReloading = true;
        return this.financesService.getCompanyFieldConfig(this.country, isInternational).pipe(
          catchError(() => {
            this.alerts.openAlerts(FH_COMPANY_MESSAGE.CONFIG_FAILED, AlertsType.error);
            return of(null);
          }),
        );
      }),
      takeUntil(this.destroy$),
    ).subscribe(res => {
      this.configReloading = false;
      // Same ordering requirement as the initial load (see ngOnInit): _applyDocumentConfig before
      // _rebuild, so its first _recompute() already knows the refreshed CMS `min`/`max`.
      this._applyDocumentConfig(res?.data ?? null);
      this._rebuild(res?.data?.fields ?? null);
      // The FormGroup instance was replaced, so the subscriptions must be re-established.
      this._watchDependencies();
      this._watchInternationalCompany();
    });
  }

  // Indexes the CMS's `documents` array (min/max per field_name) so the FILES cards can look up
  // their own field_name's limits by `field.payload`. See the `documentTypesByFieldName` doc comment
  // for what `null` vs. an empty Map means.
  private _applyDocumentConfig(config: CompanyConfig | null): void {
    const documents = config?.documents;
    this.documentTypesByFieldName = documents
      ? new Map(documents.map(doc => [doc.field_name, doc]))
      : null;
  }

  private _context(): FhPredicateContext {
    return {
      values: this.form.getRawValue(),
      country: this.country,
      byFieldId: this.byFieldId,
      isAdmin: this.permissionsService.isAdmin(),
      isFinanceApprover: this.permissionsService.checkPermission(COMPANY_PERMISSIONS.FINANCE_APPROVER),
      hasIntegrationSettlementId: !!this.company?.integration_settlement_id,
    };
  }

  // Single pass over every section and field: resolves visibility/required/editable, clears and
  // disables whatever just became hidden, and re-applies the required validator only where it
  // actually flipped. Guarded because it writes to the form it is reacting to.
  private _recompute(): void {
    if (this.recomputing || !this.form) {
      return;
    }
    this.recomputing = true;
    try {
      const ctx = this._context();
      const next = new Map<string, FhFieldState>();

      this.sections.forEach(section => {
        const sectionVisible = isSectionVisible(section, ctx);
        next.set(sectionStateKey(section.key), { visible: sectionVisible, required: false, editable: true });

        section.fields.forEach(field => {
          const visible = sectionVisible && isFieldVisible(field, ctx);
          const state: FhFieldState = {
            visible,
            required: visible && (isFieldRequired(field, ctx) || this._isDocumentTypeRequired(field)),
            editable: isFieldEditable(field, ctx),
          };
          next.set(field.controlName, state);
          if (section.kind !== FH_SECTION_KIND.REBATES) {
            this._applyFieldState(field, state);
          }
        });
      });

      this.states = next;
    } finally {
      this.recomputing = false;
    }
  }

  // A FILE field becomes required as soon as its CMS field_name has `min > 0` — per
  // docs/documents/COMPANY_DOCUMENTS_API.md, "min: cantidad mínima de documentos... requeridos
  // para una empresa (0 = no requerido)" — on top of whatever companies-form.config.json's own
  // static `required`/`required_when` says.
  private _isDocumentTypeRequired(field: CompanyFieldDescriptor): boolean {
    if (field.type !== FH_FIELD_TYPE.FILE) {
      return false;
    }
    return !!this.documentTypesByFieldName?.get(field.payload)?.min;
  }

  private _applyFieldState(field: CompanyFieldDescriptor, state: FhFieldState): void {
    const control = this.form.get(field.controlName);
    if (!control) {
      return;
    }

    if (!state.visible) {
      control.setValue(this._initialValue(field), { emitEvent: false });
      control.disable({ emitEvent: false });
      if (this.requiredApplied.get(field.controlName)) {
        control.setValidators(this.staticValidators.get(field.controlName) ?? []);
        this.requiredApplied.set(field.controlName, false);
        control.updateValueAndValidity({ emitEvent: false });
      }
      return;
    }

    if (state.editable) {
      control.enable({ emitEvent: false });
    } else {
      control.disable({ emitEvent: false });
    }

    // Only touch the validators when the required state actually changed — setValidators on every
    // keystroke would be needless churn across ~57 controls.
    if (this.requiredApplied.get(field.controlName) !== state.required) {
      const statics = this.staticValidators.get(field.controlName) ?? [];
      control.setValidators(state.required ? [Validators.required, ...statics] : statics);
      this.requiredApplied.set(field.controlName, state.required);
      control.updateValueAndValidity({ emitEvent: false });
    }
  }

  // ─── Select datasources ──────────────────────────────────────────────────────────────────────

  private _buildSelectSources(): void {
    this.sources = {};
    this.resolvers = {};

    this.sections.forEach(section => section.fields.forEach(field => {
      if (field.type !== FH_FIELD_TYPE.SELECT || !field.service) {
        return;
      }
      if (field.service === FH_SERVICE_TYPE.OWNER_SEARCH) {
        this.sources[field.controlName] = this._ownerSource();
        // The owner id IS the email, which is also the label, so ids resolve without a lookup.
        this.resolvers[field.controlName] = ids => of(ids.map(id => ({ id, name: id })));
      }
      if (field.service === FH_SERVICE_TYPE.COMPANY_GROUP) {
        this.sources[field.controlName] = this._companyGroupSource();
        this.resolvers[field.controlName] = this._companyGroupResolver();
      }
      if (field.service === FH_SERVICE_TYPE.CONTACT) {
        this.sources[field.controlName] = this._contactSource();
        this.resolvers[field.controlName] = this._contactResolver();
      }
      if (field.service === FH_SERVICE_TYPE.TYPE_OF_COMPANY) {
        this.sources[field.controlName] = this._companyTypeSource();
      }
    }));
  }

  private _ownerSource(): LazyScrollableDataSource<SelectOption> {
    return new LazyScrollableDataSource<SelectOption>((page, limit, search) =>
      this.financesService.searchOwners(this.country, page + 1, limit, search || undefined)
        .pipe(map(res => res.data.records.map(owner => ({ id: owner.email, name: owner.email })))),
    );
  }

  private _companyGroupSource(): LazyScrollableDataSource<SelectOption> {
    return new LazyScrollableDataSource<SelectOption>((page, limit, search) =>
      this.financesService.getCompanyGroups(page + 1, limit, search || undefined)
        .pipe(
          map(res => res.data.records.map(group => ({ id: String(group.id), name: group.name }))),
          catchError(() => of([] as SelectOption[])),
        ),
    );
  }

  // GET /company-type-options (same catalog backing the Companies tab's Type filter, see
  // companies.component.ts#fetchTypeOptions). No search param on this endpoint, so `search` is
  // ignored. `id` is the snake_case `code` — the same value the backend's `type_of_company` field
  // and the required_when/visible_when predicates in companies-form.config.json expect (e.g.
  // "agency").
  private _companyTypeSource(): LazyScrollableDataSource<SelectOption> {
    return new LazyScrollableDataSource<SelectOption>((page, limit) =>
      this.financesService.getCompanyTypeOptions(page + 1, limit).pipe(
        map(res => res.data.records.map(option => {
          // Remember code → numeric catalog id so _persist can send type_of_company_id too.
          this.companyTypeIdByCode.set(option.code, option.id);
          return { id: option.code, name: option.name };
        })),
        catchError(() => of([] as SelectOption[])),
      ),
    );
  }

  // GET /contacts — a global, paginated directory (no company_id needed), unlike the Zoho-backed
  // /contacts/search the PO form uses for its company-scoped "Point of contact" field. Contacts
  // aren't attached to a company until it's saved (via legal_poc_id/financial_poc_id), so this
  // list works the same whether the company exists yet or not.
  private _contactSource(): LazyScrollableDataSource<SelectOption> {
    return new LazyScrollableDataSource<SelectOption>((page, limit, search) =>
      this.financesService.listContacts(page + 1, limit, search || undefined).pipe(
        map(res => res.data.records.map(contact => ({
          id: String(contact.id),
          name: `${contact.full_name}  ·  ${contact.email}`,
        }))),
        catchError(() => of([] as SelectOption[])),
      ),
    );
  }

  // Resolves a saved company_group_id via GET /company-groups/{id} (fh-select's optionCache only
  // knows the groups its LazyScrollableDataSource has actually paged through, and edit mode can
  // load a group that lives on a page it never fetched). Falls back to the id itself so the select
  // shows something rather than going blank if the lookup fails.
  private _companyGroupResolver(): FhOptionResolver {
    return ids => forkJoin(ids.map(id => this.financesService.getCompanyGroup(Number(id)).pipe(
      map(res => ({ id: String(res.data.id), name: res.data.name })),
      catchError(() => of({ id, name: id })),
    )));
  }

  // Resolves a saved legal_poc_id/financial_poc_id via GET /contacts/{id}, same reasoning and same
  // label format as _contactSource above.
  private _contactResolver(): FhOptionResolver {
    return ids => forkJoin(ids.map(id => this.financesService.getContact(Number(id)).pipe(
      map(res => ({ id: String(res.data.id), name: `${res.data.full_name}  ·  ${res.data.email}` })),
      catchError(() => of({ id, name: id })),
    )));
  }

  // ─── Edit mode ───────────────────────────────────────────────────────────────────────────────

  private _tryPatchForm(): void {
    if (this.mode !== DETAIL_MODE.EDIT || !this.configLoaded || !this.company || this.companyLoaded) {
      return;
    }
    this.form.patchValue(this._buildFormPatch(this.company), { emitEvent: false });
    this._recompute();
    this.companyLoaded = true;
    if (this.companyId) {
      this._loadRebates(this.companyId);
    }
  }

  // Maps the loaded record onto control names by matching each descriptor's `payload` key, which
  // is what the backend returns.
  private _buildFormPatch(company: CompanyRecord): Record<string, unknown> {
    const patch: Record<string, unknown> = {};
    const record = company as unknown as Record<string, unknown>;

    this.byControlName.forEach((field, controlName) => {
      // For valuePayload fields the code lives in the valuePayload key, not the payload key.
      const sourceKey = field.valuePayload && field.type === FH_FIELD_TYPE.SELECT && !field.multiple
        ? field.valuePayload
        : field.payload;
      const value = record[sourceKey];

      if (value === undefined || value === null) {
        return;
      }
      patch[controlName] = field.type === FH_FIELD_TYPE.SELECT && !field.multiple
        ? String(value)
        : value;
    });

    return patch;
  }

  // GET /companies/{id}/agency-rebates — only ever fetched once, right after the company itself
  // loads in edit mode, purely to prefill the card. Saving no longer reads these rows back: PUT
  // /companies/:id now reconciles agency_rebate declaratively from whatever the form currently
  // holds (see company-form.payload.ts), so there is nothing left to diff against.
  private _loadRebates(companyId: number): void {
    this.financesService.listAgencyRebates(companyId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          const rebates: AgencyRebate[] = res.data ?? [];
          rebates.forEach(rebate => {
            this.rebatesArray.push(this.createRebateRow({
              id: rebate.id,
              rebate_currency: rebate.currency,
              rebate_threshold: Number(rebate.rebate_threshold),
              rebate_percentage: Number(rebate.rebate_percentage),
            }));
          });
          this.rebatesArray.updateValueAndValidity();
        },
        // Non-fatal: the rest of the company still loaded. The section simply opens empty, same as
        // before this endpoint existed.
        error: () => this.alerts.openAlerts(FH_REBATE.LOAD_FAILED, AlertsType.error),
      });
  }

  // ─── Rebates ─────────────────────────────────────────────────────────────────────────────────

  get rebatesArray(): FormArray {
    return this.form?.get(FH_FIELD.AGENCY_REBATES) as FormArray;
  }

  get rebateRows(): FhRebateRow[] {
    return (this.rebatesArray?.getRawValue() ?? []) as FhRebateRow[];
  }

  get rebatesSection(): CompanyFormSection | undefined {
    return this.sections.find(section => section.kind === FH_SECTION_KIND.REBATES);
  }

  // ─── Template helpers ────────────────────────────────────────────────────────────────────────

  get isAdmin(): boolean {
    return this.permissionsService.isAdmin();
  }

  get pageTitle(): string {
    return this.mode === DETAIL_MODE.EDIT ? FH_COMPANY_LABEL.EDIT_PAGE_TITLE : FH_COMPANY_LABEL.PAGE_TITLE;
  }

  // When editing a Draft company (reached from the list via /edit?from=list), the footer shows the
  // creation buttons (Save draft + Submit for verification) because the company has not been sent
  // to verification yet. Reaching the edit form from the read-only detail of an already-submitted
  // company instead shows the edit buttons (Cancel + Save changes), which just PUT the changes
  // without a draft/submit step. Mirrors PurchaseOrderDetailComponent.isDraftEdit.
  get isDraftEdit(): boolean {
    return this.mode === DETAIL_MODE.EDIT
      && normalizeCompanyStatus(this.company?.status) === COMPANY_STATUS.DRAFT;
  }

  // True once we know enough to decide which footer to render in edit mode: the company must have
  // loaded so isDraftEdit is reliable. In create mode there is nothing to wait for.
  get footerReady(): boolean {
    return this.mode !== DETAIL_MODE.EDIT || this.companyLoaded;
  }

  get testCompanyControlName(): string | null {
    return this.byFieldId.get(FH_FIELD.TEST_COMPANY) ?? null;
  }

  // Live value of `Is International Company?`, read straight off its control so the Tax ID format
  // message (IDE for international) tracks the toggle immediately — not only after the backend
  // config refetch updates lastIsInternational. Normalized the same way _watchInternationalCompany
  // normalizes it (real boolean, stringified "true", or legacy "yes").
  get isInternationalCompany(): boolean {
    const controlName = this.byFieldId.get(FH_FIELD.INTERNATIONAL_COMPANY);
    const value = controlName ? this.form?.get(controlName)?.value : null;
    return value === true || value === FH_FLAG.TRUE || value === FH_YES_NO.YES;
  }

  // test_company is drawn in the page header, so the Company card must not draw it again.
  get excludedControlNames(): string[] {
    const name = this.testCompanyControlName;
    return name ? [name] : [];
  }

  get canSave(): boolean {
    return !this.isSaving;
  }

  isSectionRendered(section: CompanyFormSection): boolean {
    return !!this.states.get(sectionStateKey(section.key))?.visible;
  }

  isFileSectionRequired(section: CompanyFormSection): boolean {
    const field = section.fields[0];
    return !!field && !!this.states.get(field.controlName)?.required;
  }

  // The CMS's min/max for this FILES section's field_name (its single field's `payload`), or null
  // when the CMS has no entry for it — either because the country isn't in strict mode yet, or
  // (in strict mode) because this field_name was dropped from the CMS list.
  documentConfigFor(section: CompanyFormSection): CompanyDocumentTypeConfig | null {
    const field = section.fields[0];
    if (!field) {
      return null;
    }
    return this.documentTypesByFieldName?.get(field.payload) ?? null;
  }

  get documentsStrictMode(): boolean {
    return this.documentTypesByFieldName !== null;
  }

  trackBySectionKey(_index: number, section: CompanyFormSection): string {
    return section.key;
  }

  // ─── Inline creation ─────────────────────────────────────────────────────────────────────────

  onUnderInputClick(field: CompanyFieldDescriptor): void {
    if (field.service === FH_SERVICE_TYPE.COMPANY_GROUP) {
      this._openInlineCreate(this.dialog.open(CompanyGroupDialogComponent, { width: '480px' }).afterClosed(), field);
      return;
    }
    if (field.service === FH_SERVICE_TYPE.CONTACT) {
      const ref = this.dialog.open(ContactFormDialogComponent, {
        width: '480px',
        data: { companyId: this.companyId ? String(this.companyId) : undefined },
      });
      this._openInlineCreate(ref.afterClosed(), field);
    }
  }

  onEditOptionClick({ field, option }: { field: CompanyFieldDescriptor; option: SelectOption }): void {
    const ref = this.dialog.open(ContactFormDialogComponent, {
      width: '480px',
      data: { contactId: String(option.id) },
    });
    ref.afterClosed().pipe(take(1), takeUntil(this.destroy$)).subscribe(updated => {
      if (!updated) {
        return;
      }
      this._refreshContactFields(updated);
      this.form.get(field.controlName)?.setValue(updated.id);
    });
  }

  private _refreshContactFields(updated: SelectOption): void {
    this.sections.forEach(section => section.fields.forEach(field => {
      if (field.service !== FH_SERVICE_TYPE.CONTACT) {
        return;
      }
      field.options = [updated, ...field.options.filter(o => o.id !== updated.id)];
      (this.sources[field.controlName] as LazyScrollableDataSource<SelectOption> | undefined)?.setFilterValue('');
    }));
  }

  // The created option is pushed onto the descriptor's static options so fh-select can resolve it
  // and select it immediately. That array is only a resolution cache, though — it doesn't refresh
  // what the open dropdown itself lists. `setFilterValue('')` (same trick section-buyer-info uses
  // for POINT_OF_CONTACT) resets the field's LazyScrollableDataSource and re-fetches page 0, so the
  // new record shows up there too next time it's opened, without touching the value set below.
  private _openInlineCreate(closed$: Observable<SelectOption | null>, field: CompanyFieldDescriptor): void {
    closed$.pipe(take(1), takeUntil(this.destroy$)).subscribe(created => {
      if (!created) {
        return;
      }
      field.options = [created, ...field.options];
      this.form.get(field.controlName)?.setValue(created.id);
      (this.sources[field.controlName] as LazyScrollableDataSource<SelectOption> | undefined)?.setFilterValue('');
    });
  }

  // ─── Save ────────────────────────────────────────────────────────────────────────────────────

  saveDraft(): void {
    if (!this.canSave) {
      return;
    }
    this._persist(false);
  }

  submitForm(): void {
    this.submitted = true;
    if (!this.canSave) {
      return;
    }
    this.form.markAllAsTouched();
    this._recompute();
    if (this.form.invalid) {
      return;
    }
    this._persist(true);
  }

  // Edit mode reached from the detail (not a Draft): PUT the changes and return to the detail. Runs
  // the same validation as submit, but without the submit-for-verification step. Mirrors
  // PurchaseOrderDetailComponent.saveChanges.
  saveChanges(): void {
    this.submitted = true;
    if (!this.canSave) {
      return;
    }
    this.form.markAllAsTouched();
    this._recompute();
    if (this.form.invalid) {
      return;
    }
    this._persist(false);
  }

  // Edit mode reached from the detail: discard and return to the read-only detail view.
  cancelEdit(): void {
    this.router.navigate(this._editExitRoute(), {
      queryParams: { [COMPANY_QUERY_PARAM.COUNTRY]: this.storeAux.country },
    });
  }

  // Where Cancel / save-success go after editing: back to the list when reached from the list
  // (?from=list, Draft companies), otherwise to the read-only detail view. The detail id comes
  // from the route param, NOT from this.companyId — _persist reassigns companyId from the save
  // response, so relying on it would break the return navigation when the PUT response's id shape
  // differs. The route id is immutable for the lifetime of this edit view.
  private _editExitRoute(): unknown[] {
    return this.cameFromList
      ? ['/financial-hub/companies']
      : ['/financial-hub/companies', this.route.snapshot.paramMap.get('id')];
  }

  private _persist(submit: boolean): void {
    // Documents are inline-only for a brand-new company: POST /companies is the only call that
    // can attach them (see company-form.payload.ts). Once the company has an id, they must be
    // left out of this body and synced through CompanyFilesCardComponent's own dedicated
    // endpoints instead. Agency Rebates, by contrast, are inline on BOTH POST and PUT — the
    // backend reconciles whatever array the form currently holds, so no separate sync step is
    // needed here.
    const wasNew = !this.companyId;

    const body = buildCompanyPayload({
      sections: this.sections,
      state: this.states,
      values: this.form.getRawValue(),
      country: this.country,
      includeDocuments: wasNew,
    });

    // The type_of_company select stores the snake_case code; the backend also wants the numeric
    // catalog id. Attach it when the chosen code resolves to one (populated from the paged catalog
    // or seeded from the loaded company in edit mode).
    this._attachTypeOfCompanyId(body);

    this.isSaving = true;
    const request$ = this.companyId
      ? this.financesService.updateCompany(this.companyId, body)
      : this.financesService.createCompany(body);

    request$.pipe(
      tap(res => {
        this.companyId = res.data?.id ?? this.companyId;
      }),
      switchMap(() => (submit && this.companyId
        ? this.financesService.submitCompany(this.companyId)
        : of(null))),
      takeUntil(this.destroy$),
    ).subscribe({
      next: () => {
        this.isSaving = false;
        this.alerts.openAlerts(this._successMessage(submit), AlertsType.success);
        // A direct edit (edit mode, not a Draft, no submit step) returns to where the user came
        // from — the detail, or the list when reached via ?from=list. Draft save/submit and new
        // company creation always land on the list.
        if (this.mode === DETAIL_MODE.EDIT && !this.isDraftEdit && !submit) {
          this.router.navigate(this._editExitRoute(), {
            queryParams: { [COMPANY_QUERY_PARAM.COUNTRY]: this.storeAux.country },
          });
        } else {
          this.router.navigate(['/financial-hub/companies']);
        }
      },
      error: (err) => {
        this.isSaving = false;
        this.alerts.openAlerts(this._saveErrorMessage(err), AlertsType.error);
      },
    });
  }

  // Resolves the toast text for a failed save. POST/PUT /companies use responseType 'text', so the
  // error body arrives as the raw JSON string (e.g. `{"message":"tax_id \"X\" already exists ..."}`)
  // rather than a parsed object; parsing it unescapes the JSON backslashes so the message shows
  // clean quotes. Falls back to the generic message for 5xx, an empty message, or an unparseable
  // body.
  private _saveErrorMessage(err: any): string {
    const serverMessage = this._extractServerMessage(err);
    const isServerError = err?.status >= 500;
    return serverMessage && !isServerError ? serverMessage : FH_COMPANY_MESSAGE.SAVE_FAILED;
  }

  // Pulls the backend `message` out of an HttpErrorResponse, tolerating both an already-parsed
  // object body and the raw JSON string that a text responseType yields. Returns null when there
  // is no usable message.
  private _extractServerMessage(err: any): string | null {
    const body = err?.error;
    if (body && typeof body === 'object' && typeof body.message === 'string') {
      return body.message.trim() || null;
    }
    if (typeof body === 'string') {
      try {
        const parsed = JSON.parse(body);
        if (parsed && typeof parsed.message === 'string') {
          return parsed.message.trim() || null;
        }
      } catch {
        // Not JSON — fall through to the generic message.
      }
    }
    return null;
  }

  // Adds type_of_company_id to the body when the selected type code resolves to a numeric catalog
  // id. The id is only sent if both the field is visible (its code made it into the body) and a
  // mapping exists; otherwise the key is omitted so the backend keeps its own value rather than
  // receiving a null. Mutates `body` in place.
  private _attachTypeOfCompanyId(body: Record<string, unknown>): void {
    const code = body[FH_FIELD.TYPE_OF_COMPANY];
    if (typeof code !== 'string') {
      return;
    }
    const id = this.companyTypeIdByCode.get(code);
    if (id != null) {
      body[FH_PAYLOAD.TYPE_OF_COMPANY_ID] = id;
    }
  }

  // Draft save / create → "Draft saved"; submit → "submitted"; a direct edit → "updated".
  private _successMessage(submit: boolean): string {
    if (submit) {
      return FH_COMPANY_MESSAGE.SUBMIT_OK;
    }
    if (this.mode === DETAIL_MODE.EDIT && !this.isDraftEdit) {
      return FH_COMPANY_MESSAGE.UPDATE_OK;
    }
    return FH_COMPANY_MESSAGE.SAVE_DRAFT_OK;
  }

  // Exposed so the rebates card can seed rows from a loaded company in edit mode.
  createRebateRow(seed?: Partial<FhRebateRow>): FormGroup {
    return AgencyRebatesCardComponent.createRow(this.fb, this.rebatesSection?.fields ?? [], seed);
  }
}
